// A list's domains: search, add or upload, and remove one at a time or a selection. Changes
// reach running agents within seconds (the API pushes them into their workers).
import { MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { ActionIcon, Button, Checkbox, Input } from "rizzui";

import { useT } from "../i18n";
import { WidgetCard } from "../ui/Card";
import { Mono } from "../ui/status";
import { AddDomains } from "./AddDomains";
import { changeDomains, type NetworkList } from "./api";
import type { ListChange } from "./useListChange";

export function DomainsCard({ list, change, className }: { list: NetworkList; change: ListChange; className?: string }) {
  const t = useT().network.list;
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const needle = query.trim().toLowerCase();
  const shown = list.domains.filter((d) => d.includes(needle));
  const allShown = shown.length > 0 && shown.every((d) => selected.has(d));
  const toggle = (d: string) => setSelected((s) => (s.has(d) ? new Set([...s].filter((x) => x !== d)) : new Set([...s, d])));

  async function remove(domains: string[]) {
    const done = await change(() => changeDomains(list.id, { remove: domains }), () => t.removed(domains.length));
    if (done) setSelected((s) => new Set([...s].filter((d) => !domains.includes(d))));
  }

  return (
    <WidgetCard className={className} title={t.domains} description={t.domainsHint}>
      <div className="space-y-4">
        <AddDomains listId={list.id} change={change} />
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Input
            className="sm:w-72"
            type="search"
            aria-label={t.search}
            placeholder={t.search}
            value={query}
            prefix={<MagnifyingGlass className="h-4 w-4 text-gray-500" />}
            onChange={(e) => setQuery(e.target.value)}
          />
          {selected.size > 0 ? (
            <Button variant="outline" color="danger" className="gap-1.5" onClick={() => void remove([...selected])}>
              <Trash weight="bold" className="h-4 w-4" /> {t.removeSelected(selected.size)}
            </Button>
          ) : null}
        </div>
        {list.domains.length === 0 ? <p className="m-0 text-sm text-gray-500">{t.noDomains}</p> : null}
        {list.domains.length > 0 && shown.length === 0 ? <p className="m-0 text-sm text-gray-500">{t.noMatch}</p> : null}
        {shown.length > 0 ? (
          <ul className="m-0 list-none divide-y divide-muted/70 rounded-lg border border-muted p-0" aria-label={t.domains}>
            <li className="flex items-center gap-3 bg-gray-50 px-3 py-2 dark:bg-gray-100">
              <Checkbox
                aria-label={t.selectAll}
                checked={allShown}
                onChange={() => setSelected(allShown ? new Set() : new Set([...selected, ...shown]))}
              />
              <span className="text-xs uppercase tracking-wider text-gray-500">{list.domains.length === shown.length ? t.domains : `${shown.length} / ${list.domains.length}`}</span>
            </li>
            {shown.map((d) => (
              <li key={d} className="flex items-center gap-3 px-3 py-2">
                <Checkbox aria-label={t.select(d)} checked={selected.has(d)} onChange={() => toggle(d)} />
                <Mono className="min-w-0 flex-1 truncate text-[13px]">{d}</Mono>
                <ActionIcon size="sm" variant="text" aria-label={t.removeLabel(d)} onClick={() => void remove([d])}>
                  <Trash className="h-4 w-4 text-gray-500 hover:text-red" />
                </ActionIcon>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </WidgetCard>
  );
}
